// The search box: what it draws, and how the keyboard moves through it.
//
// The ranking lives next door in search.ts and is pure. This is the half that
// touches a document — kept apart so the parts worth arguing about (what a row
// says, where the arrow keys go) can still be argued about in a test, and so
// main.ts does not grow another two hundred lines.
//
// Deliberately not a combobox in the full ARIA sense. That pattern wants an
// owned listbox, managed focus and aria-activedescendant, and gets subtly
// wrong in every browser; what is here is an input with `role="searchbox"`, a
// live region announcing the count, and rows that are buttons — which screen
// readers narrate correctly without any of the choreography.

import { escapeHtml } from './escape.ts';
import type { SearchItem, SearchResult } from './search.ts';
import { coarsePointer } from './tap-target.ts';

/** What a hit calls itself under the name, when the name is not enough. */
export const KIND_LABEL: Record<SearchItem['kind'], string> = {
  station: 'rail station',
  place: 'on this map',
  stop: 'bus stop',
  tram: 'tram stop',
  subway: 'subway station',
  route: 'bus route',
  feature: 'on the map now',
};

/**
 * Where the selection goes next.
 *
 * Wraps, because a list of eight with the arrow keys is quicker to cycle than
 * to reverse, and -1 means nothing is selected: the state the box is in as
 * soon as the query changes, when the reader's own typing is the answer and
 * pre-selecting a row would submit the wrong one on Enter.
 */
export function nextIndex(current: number, delta: number, count: number): number {
  if (count === 0) return -1;
  if (current === -1) return delta > 0 ? 0 : count - 1;
  return (current + delta + count) % count;
}

/**
 * The result list.
 *
 * `detail` where the item carries one, the kind otherwise — because "Union
 * Street" as a stop and as a camera are different answers, and the issue that
 * asked for this said so. A row with nothing under it would be the search
 * telling the reader to click and find out.
 */
export function resultsHtml(results: readonly SearchResult[], selected: number): string {
  if (results.length === 0) return '';
  return results.map(({ item }, i) => `
    <button type="button" class="sr-row${i === selected ? ' sr-on' : ''}"
            data-index="${i}"${i === selected ? ' aria-current="true"' : ''}>
      <span class="sr-name">${escapeHtml(item.name)}</span>
      <span class="sr-kind">${escapeHtml(item.detail || KIND_LABEL[item.kind])}</span>
    </button>`).join('');
}

/**
 * What the live region says.
 *
 * Counted rather than read out: a screen reader announcing eight stop names on
 * every keystroke is unusable, and the number plus what to do next is the
 * thing a reader actually needs to know has happened.
 *
 * The second sentence follows the pointer, because #search-status is visible
 * text as well as an announcement: on a phone "Use the arrow keys" was advice
 * the reader could not take, printed under a list they could not read (#69).
 * `coarsePointer()` is the same question #66 asks about a bus stop, and the
 * same answer — the primary pointer, so a laptop with a touchscreen keeps its
 * keyboard wording.
 */
export function announce(query: string, count: number, coarse = coarsePointer()): string {
  if (query.trim().length < 2) return '';
  if (count === 0) return 'Nothing found';
  const next = coarse ? 'Tap one to open it.' : 'Use the arrow keys.';
  return `${count} result${count === 1 ? '' : 's'}. ${next}`;
}

// ── The box itself ──────────────────────────────────────────────────────────

export interface SearchBox {
  /** Show it and take the caret. */
  open(): void;
  close(): void;
  toggle(): void;
}

export interface SearchDeps {
  /** The wrapper, hidden until opened. Contains the input and the list. */
  readonly root: HTMLElement;
  readonly input: HTMLInputElement;
  readonly list: HTMLElement;
  readonly status: HTMLElement;
  /** The button that opens it, so its aria-expanded can be kept honest. */
  readonly button: HTMLElement;
  /**
   * Everything searchable, fetched on first open rather than on page load.
   *
   * The stop list is 100 kB and most readers never search, so paying for it at
   * boot would be charging everybody for a feature some of them use. Called
   * again on every open: the live features come and go, and a corpus captured
   * once would offer a roadworks that finished this morning.
   */
  readonly corpus: () => Promise<readonly SearchItem[]>;
  /** Where the reader is looking, read at query time — the map moves. */
  readonly centre: () => { lat: number; lon: number } | undefined;
  readonly rank: (
    items: readonly SearchItem[],
    query: string,
    options: { centre?: { lat: number; lon: number } },
  ) => SearchResult[];
  readonly onPick: (item: SearchItem) => void;
  readonly track?: (event: string, data?: Record<string, string | number | boolean>) => void;
}

/**
 * Wire the box up.
 *
 * Ranking runs straight off the keystroke with no debounce, and that is
 * measured rather than assumed: 2,000 stops rank in one to two milliseconds,
 * so the timer would only add latency to every query to save nothing. If a
 * city ever ships a corpus large enough to drop a frame, measure first — a
 * debounce is the fix for slow, and an index is the fix for very slow, and
 * neither is the fix for this.
 */
export function wireSearch(deps: SearchDeps): SearchBox {
  const { root, input, list, status, button } = deps;
  let items: readonly SearchItem[] = [];
  let results: SearchResult[] = [];
  let selected = -1;
  /** Guards against a slow corpus landing after the box has been closed. */
  let generation = 0;

  const paint = () => {
    list.innerHTML = resultsHtml(results, selected);
    status.textContent = announce(input.value, results.length);
    // Only when there is something to say. An empty box is not a failed search.
    root.classList.toggle('has-results', results.length > 0);
  };

  const search = () => {
    results = input.value.trim().length < 2
      ? []
      : deps.rank(items, input.value, { centre: deps.centre() });
    // Reset rather than preserved: the row under the cursor after another
    // keystroke is a different place, and Enter on it would go somewhere the
    // reader never chose.
    selected = -1;
    paint();
  };

  const close = () => {
    generation += 1;
    root.hidden = true;
    root.classList.remove('has-results');
    input.value = '';
    results = [];
    selected = -1;
    list.replaceChildren();
    status.textContent = '';
    button.setAttribute('aria-expanded', 'false');
  };

  const open = () => {
    const mine = ++generation;
    root.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    input.focus();
    void deps.corpus().then((loaded) => {
      // Closed and reopened while the stop list was in flight, or closed and
      // left closed. Either way this answer is for a box that no longer exists.
      if (mine !== generation) return;
      items = loaded;
      search();
    }).catch(() => {
      if (mine !== generation) return;
      status.textContent = 'Could not load the places to search.';
    });
  };

  const pick = (index: number) => {
    const hit = results[index];
    if (!hit) return;
    deps.track?.('search-pick', { kind: hit.item.kind, rank: index });
    // Closed before the map moves, so the reader sees where they are going
    // rather than a list over it — and because the board that opens next is
    // the answer, and two panels arguing about the screen is not.
    close();
    deps.onPick(hit.item);
  };

  input.addEventListener('input', search);

  input.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      selected = nextIndex(selected, event.key === 'ArrowDown' ? 1 : -1, results.length);
      paint();
      list.querySelector('.sr-on')?.scrollIntoView({ block: 'nearest' });
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      // Nothing highlighted means the reader typed and hit Enter, which asks
      // for the best answer — not for nothing to happen.
      pick(selected === -1 ? 0 : selected);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      button.focus();
    }
  });

  list.addEventListener('click', (event) => {
    const row = (event.target as HTMLElement | null)?.closest<HTMLElement>('.sr-row');
    if (row) pick(Number(row.dataset.index));
  });

  button.addEventListener('click', () => {
    if (root.hidden) { deps.track?.('search-open'); open(); } else close();
  });

  return { open, close, toggle: () => (root.hidden ? open() : close()) };
}
